<template>
    <div class="bg-gray-100">
        <!-- Banner -->
        <div class="w-full h-96 md:h-64 relative rounded-b-[30px] md:rounded-none overflow-visible mb-32"
            style="background: linear-gradient(90deg, rgba(14, 57, 108, 1) 0%, rgba(1, 30, 65, 1) 100%);">
            <div
                class="max-w-[1200px] mx-auto w-full flex flex-col-reverse sm:flex-col-reverse md:flex-row items-end justify-center text-white px-4 sm:px-6 lg:px-8 h-full pb-4 md:mb-0 mb-20">
                <div
                    class="flex flex-col justify-end md:text-left text-center w-full md:w-2/3 order-2 mt-36 sm:mt-36 md:mt-0">
                    <h1 class="text-[36px] sm:text-[48px] md:text-[64px] font-semibold leading-tight">
                        {{ produto?.nome || '' }}
                    </h1>
                    <p class="text-lg sm:text-xl md:text-xl font-light">
                        {{ produto?.sufixo || '' }}
                    </p>
                </div>

                <div class="w-full md:w-1/3 flex justify-center order-1 -mb-32 md:-mb-48 relative z-10">
                    <img v-if="produto?.imagem_produto_path" :src="produto?.imagem_produto_path" alt="Produto"
                        class="w-3/4 sm:w-3/5 md:w-full object-contain" style="height: auto;" />
                </div>
            </div>
        </div>

        <!-- Conteúdo Principal -->
        <div class="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 z-40 md:-translate-y-[110px]">
            <div class="flex w-full">
                <div class="w-full md:w-1/3 hidden md:block"></div>
                <div class="flex flex-col w-full md:w-2/3 py-5 md:gap-6 md:ml-6">
                    <div class="flex justify-center md:justify-start flex-row gap-4">
                        <a class="flex gap-2.5 p-1.5 cursor-pointer" v-if="produto?.boletim_tecnico_path"
                            :href="produto.boletim_tecnico_path" target="_blank">
                            <Download class="text-vermelho" />
                            <span class="text-zinc-800 font-semibold">BOLETIM TÉCNICO</span>
                        </a>
                        <a class="flex gap-2.5 p-1.5 cursor-pointer" v-if="produto?.fispq_path"
                            :href="produto.fispq_path" target="_blank">
                            <Download class="text-vermelho" /> <span class="font-semibold">FISPQ</span>
                        </a>
                    </div>

                    <div class="flex-1 mt-4 md:mt-0 text-zinc-500 text-base" v-html="produto?.descricao">
                    </div>
                    <div v-if="produto?.tamanhos?.length"
                        class="flex-1 mt-4 md:mt-0 text-zinc-500 text-base flex items-center flex-wrap gap-2">
                        <span class="font-semibold mr-2">Disponível nos tamanhos:</span>
                        <span v-for="tamanho in produto?.tamanhos" :key="tamanho.slug"
                            @click="changeTamanho(tamanho.tamanho_id)"
                            class="px-2 py-0.5 border border-gray-300 rounded-lg cursor-pointer hover:bg-gray-200 transition-colors">
                            {{ tamanho.nome }}
                        </span>
                    </div>
                </div>
            </div>
        </div>

        <!-- Conteúdo Expansível -->
        <div class="flex flex-col gap-8 md:-translate-y-24 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 z-40 pb-10 md:pb-0">

            <!-- Tabs Técnicas -->
            <dl class="divide-y divide-slate-900/10 border border-gray-300 px-6 py-2 rounded-xl" v-if="tabs.length">
                <div v-for="(tab, index) in tabs" :key="index" class="py-4">
                    <dt class="cursor-pointer flex justify-between items-center text-left text-zinc-500"
                        @click="toggleTab(index)">
                        <div class="flex items-center gap-2.5">
                            <span class="font-semibold">{{ tab.title }}</span>
                        </div>
                        <svg v-if="!tab.isOpen" xmlns="http://www.w3.org/2000/svg" class="w-4 h-4" fill="none"
                            viewBox="0 0 24 24" stroke="currentColor">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
                        </svg>
                        <svg v-else xmlns="http://www.w3.org/2000/svg" class="w-4 h-4" fill="none" viewBox="0 0 24 24"
                            stroke="currentColor">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 12H4" />
                        </svg>
                    </dt>
                    <dd v-if="tab.isOpen" class="mt-2.5 text-zinc-500">
                        <p v-html="tab.content"></p>
                    </dd>
                </div>
            </dl>

            <!-- Cores Disponíveis -->
            <dl v-if="produto?.cores?.length"
                class="divide-y divide-slate-900/10 border border-gray-300 px-6 rounded-xl">
                <div class="py-4">
                    <dt class="cursor-pointer flex justify-between items-center text-left text-zinc-500"
                        @click="toggleCores">
                        <div class="flex items-center gap-2.5">
                            <PaintBucket class="w-4 h-4 inline-block mr-2" />
                            <span class="font-semibold">Cores Disponíveis</span>
                        </div>
                        <svg v-if="!isCoresOpen" xmlns="http://www.w3.org/2000/svg" class="w-4 h-4" fill="none"
                            viewBox="0 0 24 24" stroke="currentColor">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
                        </svg>
                        <svg v-else xmlns="http://www.w3.org/2000/svg" class="w-4 h-4" fill="none" viewBox="0 0 24 24"
                            stroke="currentColor">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 12H4" />
                        </svg>
                    </dt>
                    <dd v-if="isCoresOpen" class="mt-6">
                        <div class="grid grid-cols-3 gap-4 md:grid-cols-none md:flex md:flex-wrap md:gap-4">
                            <div v-for="cor in produto.cores" :key="cor.nome"
                                class="flex flex-col items-center md:items-start w-full md:w-28">
                                <div class="w-26 h-12 rounded-sm border border-gray-300"
                                    :style="{ backgroundColor: cor.cor }"></div>
                                <span
                                    class="text-xs font-semibold text-left mt-2 w-full text-center md:text-left truncate text-zinc-500"
                                    style="max-width: 7rem; display: block;" :title="cor.nome">
                                    {{ cor.nome }}
                                </span>
                            </div>
                        </div>
                        <div class="flex items-center gap-2 mt-6">
                            <TriangleAlert class="w-5 h-5 sm:w-8 sm:h-8 md:w-5 md:h-5 text-yellow-500 hidden sm:block" />
                            <span class="text-sm text-[#343434]/40">As cores deste site são apenas referências, podendo
                                existir divergências entre as cores apresentadas na tela e as cores reais dos produtos
                                comercializados.</span>
                        </div>
                    </dd>
                </div>
            </dl>

            <hr class="border-gray-300 my-4" v-if="produto?.duvidas?.length" />

            <!-- Dúvidas Frequentes -->
            <div v-if="produto?.duvidas?.length">
                <div class="flex items-center gap-2.5 mb-2 text-zinc-500">
                    <MessageCircleQuestion class="text-vermelho" />
                    <h1 class="text-2xl">Dúvidas Frequentes</h1>
                </div>

                <dl class="space-y-6 divide-y divide-slate-900/10">
                    <div v-for="(duvida, index) in produto.duvidas" :key="index" class="py-4">
                        <dt class="cursor-pointer flex justify-between text-left text-slate-900"
                            @click="duvida.isOpen = !duvida.isOpen">
                            <span class="text-base font-semibold text-zinc-500 leading-6">{{ duvida.pergunta }}</span>
                            <svg v-if="!duvida.isOpen" xmlns="http://www.w3.org/2000/svg" class="w-4 h-4" fill="none"
                                viewBox="0 0 24 24" stroke="currentColor">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                    d="M12 4v16m8-8H4" />
                            </svg>
                            <svg v-else xmlns="http://www.w3.org/2000/svg" class="w-4 h-4" fill="none"
                                viewBox="0 0 24 24" stroke="currentColor">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 12H4" />
                            </svg>
                        </dt>
                        <dd v-if="duvida.isOpen" class="mt-2.5 text-zinc-500">
                            <p>{{ duvida.resposta }}</p>
                        </dd>
                    </div>
                </dl>
            </div>

        </div>
    </div>
</template>

<script setup>
import { ref, onMounted, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { Download, MessageCircleQuestion, PaintBucket, TriangleAlert } from 'lucide-vue-next'
import { useLoading } from 'vue-loading-overlay'
import axios from 'axios'
import { Warning } from 'postcss'

const produto = ref(null)
const route = useRoute()
const router = useRouter()
const isCoresOpen = ref(false)
const $loading = useLoading()
const tabs = ref([])

const toggleTab = (index) => {
    tabs.value[index].isOpen = !tabs.value[index].isOpen
}

const toggleCores = () => {
    isCoresOpen.value = !isCoresOpen.value
}

const changeTamanho = (tamanhoId) => {
    const produtoSlug = route.params.slug
    router.push(`/produto/${produtoSlug}/${tamanhoId}`)
}


const fetchProduto = async () => {
    const loader = $loading.show()
    try {
        const produtoSlug = route.params.slug
        const tamanhoId = route.params.tamanhoSlug // que agora é o ID real

        let url = `/api/produto/${produtoSlug}`
        if (tamanhoId) {
            url += `/${tamanhoId}`
        }

        const { data } = await axios.get(url)
        produto.value = data

        tabs.value = [
            { title: 'Indicação', content: data.indicacao, isOpen: false },
            { title: 'Preparo', content: data.preparo, isOpen: false },
            { title: 'Aplicação', content: data.aplicacao, isOpen: false },
            { title: 'Rendimento', content: data.rendimento, isOpen: false },
            { title: 'Estocagem', content: data.estocagem, isOpen: false },
            { title: 'Composição', content: data.composicao, isOpen: false },
            { title: 'Recomendação', content: data.recomendacao, isOpen: false }
        ].filter(tab => tab.content)

        if (data.duvidas) {
            produto.value.duvidas = data.duvidas.map(d => ({ ...d, isOpen: false }))
        }
    } catch (error) {
        console.error('Erro ao buscar produto:', error)
    } finally {
        loader.hide()
    }
}

watch(() => route.params, fetchProduto, { immediate: true })
</script>
